
import Vue from 'vue';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {IRoute} from '@/model/Route/routeInterfaces';
import {IOnOffHandlers} from 'bonomo/dist';
import {UrlHelper} from '@/helpers/UrlHelper';
import {BetterDataTableHeader} from '@/model/betterVuetifyInterfaces';
import CreateButton from '@/components/shared/CreateButton.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import RoutePageHeader from '@/components/route/RoutePageHeader.vue';
import {PAGINATION_PER_PAGE_OPTIONS} from '@/constants/pagination';

const routeModule = namespace('routeModule');

@Component({
  components: {CreateButton, PaginationBar, RoutePageHeader},
})
export default class RouteList extends Vue {
  @routeModule.Getter('routes') protected routes!: IRoute[];
  @routeModule.Getter('currentPage') protected currentPage!: number;
  @routeModule.Getter('lastPage') protected lastPage!: number;
  @routeModule.Getter('perPage') protected perPage!: number;
  @routeModule.Getter('total') protected total!: number;
  @routeModule.Action('fetchRoutes') protected fetchRoutes!: (params?: any) => any;
  @routeModule.Action('deleteRoute') protected deleteRouteAction!: (routeId: number) => any;

  protected loading: boolean = false;
  protected statusFilter: string = '';
  protected showDeleteDialog: boolean = false;
  protected deleting: boolean = false;
  protected deletingRoute: IRoute | null = null;
  protected ons: IOnOffHandlers = {};
  protected readonly perPageOptions: number[] = PAGINATION_PER_PAGE_OPTIONS;

  protected get headers(): BetterDataTableHeader[] {
    return [
      {text: 'ID', value: 'id', sortable: false},
      {text: this.$t('routes.list.driver'), value: 'driver_name', sortable: false},
      {text: this.$t('routes.list.start_address'), value: 'start_address', sortable: false},
      {text: this.$t('routes.list.end_address'), value: 'end_address', sortable: false},
      {text: this.$t('routes.list.hops'), value: 'hops_count', sortable: false, align: 'center'},
      {text: this.$t('common.status'), value: 'status', sortable: false},
      {text: this.$t('common.date'), value: 'created_at', sortable: false},
      {text: '', value: 'actions', sortable: false, align: 'end'},
    ];
  }

  protected get statusOptions() {
    return [
      {text: this.$t('routes.status.in_delivery'), value: 'status_route_in_delivery'},
      {text: this.$t('routes.status.finished'), value: 'status_route_finished'},
    ];
  }

  protected get useCards(): boolean {
    return this.$vuetify.breakpoint.smAndDown;
  }

  protected get canAdmin(): boolean {
    return this.$canAction('routes/admin');
  }

  protected blockedRowClass(item: IRoute): string {
    return this.canAdmin && !item.is_visible ? 'route-list__row--blocked' : '';
  }

  protected get canCreate(): boolean {
    return this.$canAction('routes/create') || this.$canAction('routes/admin');
  }

  protected get canEdit(): boolean {
    return this.$canAction('routes/edit') || this.$canAction('routes/admin');
  }

  protected get canDelete(): boolean {
    return this.$canAction('routes/delete') || this.$canAction('routes/admin');
  }

  protected get oldBackofficeUrl(): string {
    return UrlHelper.mayorOldRoutes();
  }

  created() {
    this.ons = this.$ons({
      'backend.call.OK.routesLoaded': this.onRoutesLoaded,
      'backend.call.KO.routesLoaded': this.onRoutesLoaded,
      'backend.call.OK.routeDeleted': this.onRouteDeleted,
      'backend.call.KO.routeDeleted': this.onDeleteError,
    });
    this.load();
  }

  beforeDestroy() {
    this.$offs(this.ons);
  }

  protected load() {
    this.loading = true;
    const params: any = {per_page: this.perPage, page: this.currentPage};
    if (this.statusFilter) params.status = this.statusFilter;
    this.fetchRoutes(params);
  }

  protected onRoutesLoaded() {
    this.loading = false;
  }

  protected onStatusChange() {
    this.load();
  }

  protected onPageChange(page: number) {
    this.fetchRoutes({per_page: this.perPage, page, status: this.statusFilter || undefined});
  }

  protected onPerPageChange(value: number) {
    this.fetchRoutes({per_page: value, page: 1, status: this.statusFilter || undefined});
  }

  protected onCreate() {
    this.$router.push({name: 'routeCreate'});
  }

  protected openRoute(route: IRoute) {
    this.$router.push({name: 'route', params: {routeId: String(route.id)}});
  }

  protected editRoute(route: IRoute) {
    this.$router.push({name: 'routeEdit', params: {routeId: String(route.id)}});
  }

  protected confirmDelete(route: IRoute) {
    this.deletingRoute = route;
    this.showDeleteDialog = true;
  }

  protected onDeleteRoute() {
    if (!this.deletingRoute) return;
    this.deleting = true;
    this.deleteRouteAction(this.deletingRoute.id);
  }

  protected onRouteDeleted() {
    this.deleting = false;
    this.showDeleteDialog = false;
    this.deletingRoute = null;
    this.load();
  }

  protected onDeleteError() {
    this.deleting = false;
  }
}
